/**
 * Pure result mappers: Slice 5 workloads `ValidationReport` / `RunView` →
 * the existing local `ImportResult` shape consumed by `WizardProvider` /
 * `ReviewScreen` / `ValidationErrorsScreen` / `ImportResultsScreen`.
 *
 * LADR-039 (#440) Step 12 — see `.plans/ladr-039-contract-mapping.md`'s pay
 * components vertical ("Durable hold + run" table and residual mismatch
 * #115) for the authoritative field mapping this module implements. Mirrors
 * `src/work-patterns/hooks/WorkPatternImport/workPatternWorkloadsResultMappers.ts`
 * (Step 7) — no React/hooks here; safe to unit test directly and reuse from
 * `usePayComponentImportWorkloadsManager` (Step 12) without pulling in
 * TanStack Query.
 *
 * **Residual — no post-run per-row detail.** `RunView` carries only
 * `pushed_versions` (which entity types/etags were pushed), never a per-row
 * outcome, unlike Track 1's `useWorkflowCommit`/`toImportResult`. A run
 * failure therefore cannot be rendered as a populated `errors[]` table —
 * `mapRunViewToImportResult` returns `null` for any non-`succeeded` status
 * and callers must surface a manager-level error string instead (see
 * `usePayComponentImportWorkloadsManager`'s `managerError`).
 *
 * **Residual — no `warnings` home.** `ValidationReport.warnings` (Christmas
 * dry-run advisories) have no field on the wizard's `ImportResult` schema to
 * carry them (unlike `WorkPatternImportResult.warnings` in the work-patterns
 * vertical) — the pre-existing Track 1 `ImportResult` contract
 * (`src/pay-component-import/types/import-result.ts`) never had a warnings
 * field either, so this is not a regression introduced by the cutover;
 * pay-component validation warnings are simply dropped at this boundary.
 */
import {
  MigrationRunStatus,
  type RowValidationError,
  type RunView,
  type ValidationReport,
} from "../../generated/core-api";
import type { ImportResult, ImportValidationError } from "../types";
import type { HeldPayComponentRow } from "./payComponentEntityPatch";

/**
 * `RowValidationError.row_index` is a zero-based **positional** index into
 * whatever `canonical_entities` array was validated (per the generated
 * docstring — "Zero-based index into the rows array") — not the row's own
 * (1-based, Track-1-style) `rowIndex` field — so the held row is looked up
 * by array position, exactly like `workPatternNameForRow` in the Step 7
 * mapper. Falls back to empty strings if the index is out of range, which
 * should not happen in practice but keeps the mapper total.
 */
function heldRowAt(
  heldRows: HeldPayComponentRow[],
  rowIndex: number,
): Pick<HeldPayComponentRow, "employeeCode" | "componentName"> {
  return heldRows[rowIndex] ?? { employeeCode: "", componentName: "" };
}

/**
 * Groups Track 2's one-`RowValidationError`-per-(row, column) shape into the
 * domain's one-`ImportValidationError`-per-row shape, joining multiple
 * column messages for the same row with `"; "` — the same join convention
 * the Step 7 work-patterns mapper uses, and the same convention
 * `useWorkflowCommit`'s `formatRowError`/row-error merging follows for
 * Track 1. Converts the 0-based `row_index` to the domain's 1-based `row`
 * (confirmed against `resultsExport.ts`'s `buildFailedRowsCsv`, which looks
 * up `reasonsByRow.get(i + 1)` for a 0-based `i` into the header-stripped
 * data lines — i.e. `ImportValidationError.row` counts from 1 at the first
 * data row, not 0).
 */
export function mapRowValidationErrorsToImportErrors(
  errors: RowValidationError[] | undefined,
  heldRows: HeldPayComponentRow[],
): ImportValidationError[] {
  const messagesByRow = new Map<number, string[]>();
  for (const error of errors ?? []) {
    const bucket = messagesByRow.get(error.row_index);
    if (bucket) {
      bucket.push(error.message);
    } else {
      messagesByRow.set(error.row_index, [error.message]);
    }
  }
  return Array.from(messagesByRow.entries())
    .sort(([a], [b]) => a - b)
    .map(([rowIndex, messages]) => {
      const held = heldRowAt(heldRows, rowIndex);
      return {
        row: rowIndex + 1,
        employeeCode: held.employeeCode,
        componentCode: held.componentName,
        error: messages.join("; "),
      };
    });
}

/**
 * Maps the validate-phase `ValidationReport` (from `useOnboardingValidation`'s
 * polled `ValidationView.report`) to the local `ImportResult` shape — the
 * pre-run outcome the manager surfaces while deciding whether it is safe to
 * proceed to `createOnboardingMigrationRun`.
 *
 * `report` is nullable to cover `ValidationView.report` being `null`/absent
 * while the validation job is still queued/running; callers are expected to
 * only treat the result as final once the job status is terminal (mirroring
 * the Step 7/8 work-patterns convention). A terminal `JobStatus.succeeded`
 * validation *job* only means the job ran to completion — the *report* may
 * still carry blocking errors (the entity record then transitions to
 * `validation_failed`, not `validated`), which is why this always returns a
 * populated result rather than `null`: `status: "invalid"` when `errors` is
 * non-empty, `"valid"` otherwise, mirroring `useImportPayComponents`'
 * pre-flight status vocabulary.
 */
export function mapValidationReportToImportResult(
  report: ValidationReport | null | undefined,
  heldRows: HeldPayComponentRow[],
): ImportResult {
  const errors = mapRowValidationErrorsToImportErrors(
    report?.errors,
    heldRows,
  );
  const warnings = mapRowValidationErrorsToImportErrors(
    report?.warnings,
    heldRows,
  );
  const displayErrors = [...errors, ...warnings];
  return {
    status: errors.length > 0 ? "invalid" : "valid",
    committed: 0,
    failed: errors.length,
    errors: displayErrors,
    unmapped: [],
    mappingsSaved: 0,
  };
}

/**
 * Maps the commit-phase `RunView` (from `useOnboardingMigrationRun`'s polled
 * result) to the local `ImportResult` shape — the Track 2 replacement for
 * `useWorkflowCommit`'s `toImportResult`.
 *
 * Residual mismatch (contract note, pay-components "Durable hold + run"
 * table / #115): unlike Track 1's per-row commit result, `RunView` carries
 * **no per-row outcome** — only `pushed_versions` (which entity types/etags
 * were pushed) and an overall `status`. By the time a run is `POST`ed the
 * held rows are assumed already-validated (the manager only invokes a run
 * after a clean, error-free validate pass), so:
 * - `status: "succeeded"` → every held row is reported committed (there is
 *   nothing more granular to report).
 * - any other status (`"failed"`, or a non-terminal status passed in error)
 *   → returns `null`. A whole-run failure is a coarser signal than Track 1's
 *   per-row `errors[]` and has no row to attach itself to; callers must
 *   surface it as a manager-level error instead of a structured result,
 *   mirroring the Step 7 work-patterns mapper's
 *   `mapRunViewToWorkPatternImportResult`.
 */
export function mapRunViewToImportResult(
  run: RunView,
  heldRows: HeldPayComponentRow[],
): ImportResult | null {
  if (run.status !== MigrationRunStatus.succeeded) {
    return null;
  }
  return {
    status: "committed",
    committed: heldRows.length,
    failed: 0,
    errors: [],
    unmapped: [],
    mappingsSaved: 0,
  };
}
